import type { PuzzleData } from '../types';

export interface GameEvents {
  'agent:spawned': { agentId: string };
  'agent:crashed': { agentId: string };
  'agent:corrected': { agentId: string; driftAtCorrection: number };
  'agent:despawned': { agentId: string };
  'task:assigned': { taskId: string; agentId: string };
  'task:expired': { taskId: string };
  'task:completed': { agentId: string; taskId: string };
  'validation:requested': { agentId: string; taskId: string; puzzleData: PuzzleData };
  'validation:decided': { approved: boolean; correct: boolean };
  'game:start': void;
  'game:over': { reason: 'lives' | 'overflow' };
  'game:restart': void;
  'combo:broken': void;
  'combo:milestone': { level: number };
  'compute:changed': { amount: number; total: number };
}

type EventCallback<T> = T extends void ? () => void : (data: T) => void;

export class EventBus {
  private listeners = new Map<string, Set<Function>>();

  on<K extends keyof GameEvents>(event: K, callback: EventCallback<GameEvents[K]>): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  off<K extends keyof GameEvents>(event: K, callback: EventCallback<GameEvents[K]>): void {
    this.listeners.get(event)?.delete(callback);
  }

  emit<K extends keyof GameEvents>(
    event: K,
    ...args: GameEvents[K] extends void ? [] : [GameEvents[K]]
  ): void {
    this.listeners.get(event)?.forEach(cb => {
      if (args.length > 0) {
        cb(args[0]);
      } else {
        cb();
      }
    });
  }

  clear(): void {
    this.listeners.clear();
  }
}
